/**
 * Network Logger Service
 * 
 * Intercepts and logs failed network requests for debugging purposes.
 * Captures fetch failures, XHR failures (including Amplify), GraphQL errors, 
 * and provides manual logging for other HTTP clients.
 * 
 * Only captures when in debug mode (DEV/STAGE environment or showDebug URL param).
 */

class NetworkLogger {
  constructor() {
    this.failedRequests = [];
    this.maxRequests = 100;
    this.isInitialized = false;
    this.debugModeActive = false;
    this.originalFetch = null;
    this.OriginalXHR = null;
  }
  
  /**
   * Checks if debug mode is active
   */
  isDebugModeActive() {
    const stack = process.env.FEATURE_FLAGS_STACK;
    if (stack === 'DEV' || stack === 'STAGE') {
      return true;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = urlParams.get('showDebug');
    return showDebug === '1' || showDebug === 'true';
  }
  
  /**
   * Initializes fetch interception
   * Only activates if debug mode is enabled
   */
  init() {
    if (this.isInitialized) {
      return;
    }
    
    this.debugModeActive = this.isDebugModeActive();
    
    if (!this.debugModeActive) {
      return;
    }
    
    this.isInitialized = true;
    this.interceptFetch();
    this.interceptXHR();
  }
  
  /**
   * Intercepts native fetch API
   */
  interceptFetch() {
    this.originalFetch = window.fetch.bind(window);
    
    // Intercept all fetch requests
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      const startTime = Date.now();
      const method = options.method || 'GET';
      
      try {
        const response = await this.originalFetch(...args);
        const duration = Date.now() - startTime;
        
        // Capture failed HTTP responses (4xx, 5xx)
        if (!response.ok) {
          let errorBody = null;
          try {
            const clonedResponse = response.clone();
            errorBody = await clonedResponse.text();
          } catch (e) {
            // Ignore body read errors
          }
          
          this.captureFailedRequest({
            url: this.sanitizeUrl(url),
            method,
            status: response.status,
            statusText: response.statusText,
            type: 'HTTP_ERROR',
            duration,
            errorBody: this.truncateBody(errorBody),
          });
        }
        
        // Check for GraphQL errors in successful responses
        const urlStr = typeof url === 'string' ? url : url.toString();
        if (response.ok && this.isGraphQLUrl(urlStr)) {
          try {
            const clonedResponse = response.clone();
            const body = await clonedResponse.json();
            if (body.errors && body.errors.length > 0) {
              this.captureFailedRequest({
                url: this.sanitizeUrl(url),
                method: 'POST',
                status: 200,
                type: 'GRAPHQL_ERROR',
                duration,
                graphqlErrors: body.errors.map(e => ({
                  message: e.message,
                  path: e.path,
                })),
              });
            }
          } catch (e) {
            // Not JSON or couldn't parse - ignore
          }
        }
        
        return response;
      } catch (error) {
        // Capture network failures (timeout, connection refused, CORS, etc.)
        this.captureFailedRequest({
          url: this.sanitizeUrl(url),
          method,
          type: 'NETWORK_ERROR',
          duration: Date.now() - startTime,
          errorMessage: error.message,
          errorName: error.name,
        });
        throw error; // Re-throw to preserve original behavior
      }
    };
  }
  
  /**
   * Intercepts XMLHttpRequest (used by Amplify and other libraries)
   */
  interceptXHR() {
    const self = this;
    this.OriginalXHR = window.XMLHttpRequest;
    
    function InterceptedXHR() {
      const xhr = new self.OriginalXHR();
      let requestUrl = '';
      let requestMethod = 'GET';
      let startTime = 0;
      
      // Store original open
      const originalOpen = xhr.open.bind(xhr);
      xhr.open = function(method, url, ...rest) {
        requestMethod = method;
        requestUrl = url;
        return originalOpen(method, url, ...rest);
      };
      
      // Store original send
      const originalSend = xhr.send.bind(xhr);
      xhr.send = function(body) {
        startTime = Date.now();
        return originalSend(body);
      };
      
      // Listen for completed requests
      xhr.addEventListener('loadend', function() {
        const duration = Date.now() - startTime;
        
        // Capture HTTP errors (4xx, 5xx)
        if (xhr.status >= 400) {
          self.captureFailedRequest({
            url: self.sanitizeUrl(requestUrl),
            method: requestMethod,
            status: xhr.status,
            statusText: xhr.statusText,
            type: 'HTTP_ERROR',
            duration,
            errorBody: self.truncateBody(xhr.responseText),
            source: 'XHR',
          });
        }
        
        // Check for GraphQL errors in successful responses
        if (xhr.status >= 200 && xhr.status < 300 && self.isGraphQLUrl(requestUrl)) {
          try {
            const responseBody = JSON.parse(xhr.responseText);
            if (responseBody.errors && responseBody.errors.length > 0) {
              self.captureFailedRequest({
                url: self.sanitizeUrl(requestUrl),
                method: requestMethod,
                status: xhr.status,
                type: 'GRAPHQL_ERROR',
                duration,
                graphqlErrors: responseBody.errors.map(e => ({
                  message: e.message,
                  path: e.path,
                  errorType: e.errorType,
                })),
                source: 'XHR',
              });
            }
          } catch (e) {
            // Not JSON or couldn't parse - ignore
          }
        }
      });
      
      // Listen for network errors
      xhr.addEventListener('error', function() {
        self.captureFailedRequest({
          url: self.sanitizeUrl(requestUrl),
          method: requestMethod,
          type: 'NETWORK_ERROR',
          duration: Date.now() - startTime,
          errorMessage: 'Network request failed',
          source: 'XHR',
        });
      });
      
      // Listen for timeout errors
      xhr.addEventListener('timeout', function() {
        self.captureFailedRequest({
          url: self.sanitizeUrl(requestUrl),
          method: requestMethod,
          type: 'NETWORK_ERROR',
          duration: Date.now() - startTime,
          errorMessage: 'Request timed out',
          errorName: 'TimeoutError',
          source: 'XHR',
        });
      });
      
      return xhr;
    }
    
    // Preserve static properties and prototype
    InterceptedXHR.prototype = this.OriginalXHR.prototype;
    Object.keys(this.OriginalXHR).forEach(key => {
      InterceptedXHR[key] = this.OriginalXHR[key];
    });
    
    // Replace global XMLHttpRequest
    window.XMLHttpRequest = InterceptedXHR;
  }
  
  /**
   * Checks if URL is likely a GraphQL endpoint
   */
  isGraphQLUrl(url) {
    return url.includes('graphql') || 
           url.includes('appsync');
  }
  
  /**
   * Sanitizes URL by removing sensitive query parameters
   */
  sanitizeUrl(url) {
    try {
      const urlStr = typeof url === 'string' ? url : url.toString();
      const urlObj = new URL(urlStr, window.location.origin);
      // Remove potentially sensitive params
      urlObj.searchParams.delete('token');
      urlObj.searchParams.delete('apiKey');
      urlObj.searchParams.delete('key');
      return urlObj.toString();
    } catch (e) {
      return String(url);
    }
  }
  
  /**
   * Truncates response body to prevent memory issues
   */
  truncateBody(body, maxLength = 500) {
    if (!body) return null;
    if (body.length <= maxLength) return body;
    return body.substring(0, maxLength) + '... [truncated]';
  }
  
  /**
   * Captures a failed request
   */
  captureFailedRequest(details) {
    if (!this.debugModeActive) {
      return;
    }
    
    const entry = {
      timestamp: new Date().toISOString(),
      ...details,
    };
    
    this.failedRequests.push(entry);
    
    // Limit storage
    if (this.failedRequests.length > this.maxRequests) {
      this.failedRequests.shift();
    }
  }
  
  /**
   * Manually log a GraphQL error (for superagent-based requests)
   * Call this from graphqlRequest functions when errors occur
   * 
   * @param {string} serviceName - Name of the service (e.g., 'StudentInformationService')
   * @param {string} url - Request URL
   * @param {string} operationName - GraphQL operation name (optional)
   * @param {Object} error - Error object or GraphQL errors array
   * @param {number} duration - Request duration in ms (optional)
   */
  logGraphQLError(serviceName, url, operationName, error, duration = null) {
    if (!this.debugModeActive) {
      return;
    }
    
    const isGraphQLErrors = Array.isArray(error);
    
    this.captureFailedRequest({
      url: this.sanitizeUrl(url),
      method: 'POST',
      type: isGraphQLErrors ? 'GRAPHQL_ERROR' : 'GRAPHQL_REQUEST_ERROR',
      serviceName,
      operationName,
      duration,
      graphqlErrors: isGraphQLErrors ? error.map(e => ({
        message: e.message,
        path: e.path,
      })) : null,
      errorMessage: !isGraphQLErrors ? (error.message || String(error)) : null,
    });
  }
  
  /**
   * Returns all failed requests
   */
  getFailedRequests() {
    return [...this.failedRequests];
  }
  
  /**
   * Gets statistics about failed requests
   */
  getStats() {
    const stats = {
      total: this.failedRequests.length,
      httpErrors: 0,
      graphqlErrors: 0,
      networkErrors: 0,
      bySource: {
        fetch: 0,
        xhr: 0,
        manual: 0,
      },
    };
    
    this.failedRequests.forEach(req => {
      switch (req.type) {
        case 'HTTP_ERROR':
          stats.httpErrors++;
          break;
        case 'GRAPHQL_ERROR':
        case 'GRAPHQL_REQUEST_ERROR':
          stats.graphqlErrors++;
          break;
        case 'NETWORK_ERROR':
          stats.networkErrors++;
          break;
      }
      
      // Track source
      if (req.source === 'XHR') {
        stats.bySource.xhr++;
      } else if (req.serviceName) {
        stats.bySource.manual++;
      } else {
        stats.bySource.fetch++;
      }
    });
    
    return stats;
  }
  
  /**
   * Exports failed requests to a downloadable file
   */
  exportFailedRequests() {
    const content = `Network Errors Export
Generated: ${new Date().toISOString()}
URL: ${window.location.href}
Total Failed Requests: ${this.failedRequests.length}

${'='.repeat(80)}

${this.failedRequests.map(req => {
  let output = `[${req.timestamp}] ${req.type}
URL: ${req.url}
Method: ${req.method}`;
  
  if (req.status) output += `\nStatus: ${req.status} ${req.statusText || ''}`;
  if (req.serviceName) output += `\nService: ${req.serviceName}`;
  if (req.operationName) output += `\nOperation: ${req.operationName}`;
  if (req.duration) output += `\nDuration: ${req.duration}ms`;
  if (req.errorMessage) output += `\nError: ${req.errorMessage}`;
  if (req.errorName) output += `\nError Type: ${req.errorName}`;
  if (req.errorBody) output += `\nResponse Body: ${req.errorBody}`;
  if (req.graphqlErrors) {
    output += `\nGraphQL Errors:\n${req.graphqlErrors.map(e => `  - ${e.message}${e.path ? ` (path: ${e.path.join('.')})` : ''}`).join('\n')}`;
  }
  
  return output;
}).join('\n\n' + '-'.repeat(80) + '\n\n')}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `network-errors-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  /**
   * Clears all logged requests
   */
  clearLogs() {
    this.failedRequests = [];
  }
}

// Create singleton instance
const networkLogger = new NetworkLogger();

export default networkLogger;

